import { cryptoUtil } from "./crypto";

/**
 * 서버와 교환한 세션 AES 키를 메모리에만 보관하는 홀더
 *
 * - 키는 내보낼 수 없는(non-extractable) CryptoKey로 변환해 보관하며, 브라우저 저장소에는 쓰지 않습니다.
 * - 새로고침·새 탭처럼 메모리가 비면 등록된 복원 함수(쿠키 기반 키 재교환)로 다시 받아옵니다.
 * - 동시에 여러 요청이 복원을 시도해도 재교환은 한 번만 수행합니다.
 */
export class SessionKeyHolder {
  private key: CryptoKey | null = null;
  private restoring: Promise<CryptoKey | null> | null = null;
  private restorer: (() => Promise<boolean>) | null = null;

  constructor(private readonly importKey: (rawKey: string) => Promise<CryptoKey>) {}

  /**
   * 메모리가 비었을 때 키를 다시 받아오는 함수를 등록합니다.
   * 복원 함수는 성공 시 setKey 또는 setRawKey로 키를 저장하고 true를 반환해야 합니다.
   */
  setRestorer(restorer: () => Promise<boolean>): void {
    this.restorer = restorer;
  }

  /**
   * 서버에서 받은 원본 키를 CryptoKey로 변환해 보관합니다.
   */
  async setRawKey(rawKey: string): Promise<void> {
    this.key = await this.importKey(rawKey);
  }

  /**
   * 이미 만들어진 CryptoKey 를 그대로 보관합니다 (unwrapKey 로 푼 서버 키).
   */
  setKey(key: CryptoKey): void {
    this.key = key;
  }

  getKey(): CryptoKey | null {
    return this.key;
  }

  /**
   * 보관 중인 키를 반환하고, 없으면 복원 함수로 다시 받아옵니다.
   * @returns 키, 복원할 수 없으면 null
   */
  async getOrRestoreKey(): Promise<CryptoKey | null> {
    if (this.key) {
      return this.key;
    }

    if (!this.restorer) {
      return null;
    }

    if (!this.restoring) {
      const restorer = this.restorer;
      this.restoring = (async () => {
        try {
          const isRestored = await restorer();
          return isRestored ? this.key : null;
        } catch (error: unknown) {
          console.error("[SessionKeyHolder] :: getOrRestoreKey :: 키 복원 실패", error);
          return null;
        } finally {
          this.restoring = null;
        }
      })();
    }

    return this.restoring;
  }

  /**
   * 보관 중인 키를 메모리에서 제거합니다 (로그아웃·세션 만료).
   */
  clear(): void {
    this.key = null;
  }
}

export const sessionKeyHolder = new SessionKeyHolder((rawKey) => cryptoUtil.importAesKey(rawKey));
